import { SkyCard, SkyText } from "@skypointcloud/skypoint-ui";
import {
  BINDING_EXPIRY_FIELD_LABELS,
  BINDING_EXPIRY_FIELDS,
  BINDING_EXPIRY_SECTION,
  CREDENTIAL_TARGET_LABELS,
  type BindingExpiryField,
} from "@shared/types/credentials";
import type { InstanceBindingView } from "@shared/types/instances";
import { credentialTarget, useCredentials, useSetBindingExpiry } from "@/hooks/use-credentials";
import { bindingSecretStored } from "@/lib/secretStored";
import { CredentialExpiryEditor } from "./CredentialExpiryEditor";
import { CredentialAttentionBadge, CredentialCheckLine, expirySentence } from "./CredentialStatus";

/**
 * The destination's credentials, one row per section that holds one (2026-09-28 cred-check
 * design, "UI"): the stage SAS, the warehouse key pair and the dbt repository token. Each says
 * what the daily check found and when the credential expires. A SAS's own expiry (`se`) is read
 * from the token and shown read-only; a key pair or a git token without a provider date takes a
 * typed one (`instance.binding.write`).
 *
 * There is no "Use the latest version" action: every read resolves the latest enabled Key Vault
 * version (owner answer c), so a rotation in the vault reaches the next load by itself.
 */
export function DestinationCredentials({
  binding,
  canEdit,
}: {
  binding: InstanceBindingView;
  canEdit: boolean;
}) {
  const credentials = useCredentials();
  const setExpiry = useSetBindingExpiry();
  // SEC-17: a reader without instance.settings.write gets no pointers, only whether each is stored.
  const stored = BINDING_EXPIRY_FIELDS.filter((field) => bindingSecretStored(binding, field));
  if (stored.length === 0 || !credentials.data) return null;
  const view = credentials.data;

  return (
    <SkyCard className="flex flex-col gap-4 p-4" testId="destination-credentials">
      <div className="flex flex-col gap-1">
        <SkyText as="h3" size={300} weight="semibold">
          Credential checks
        </SkyText>
        <SkyText size={200} color="muted" block>
          skyData checks each credential once a day and warns 30 and 7 days before one expires. A
          new version you add is used from the next import on.
        </SkyText>
      </div>
      {stored.map((field: BindingExpiryField) => {
        const section = BINDING_EXPIRY_SECTION[field];
        const target = credentialTarget(view, section, binding.instanceId);
        const expiry = target?.expiries.find((row) => row.field === field) ?? null;
        const fromToken = expiry !== null && expiry.source !== "entered";
        return (
          <div
            key={field}
            className="flex flex-col gap-2 border-t border-border pt-3"
            data-testid={`destination-credential-${section}`}
          >
            <div className="flex flex-wrap items-center gap-2">
              <SkyText size={300} weight="semibold">
                {`${CREDENTIAL_TARGET_LABELS[section]}: ${BINDING_EXPIRY_FIELD_LABELS[field]}`}
              </SkyText>
              <CredentialAttentionBadge
                target={target}
                testId={`destination-credential-badge-${section}`}
              />
            </div>
            <CredentialCheckLine
              target={target}
              checksEnabled={view.checksEnabled}
              testId={`destination-credential-check-${section}`}
            />
            {fromToken ? (
              <SkyText
                size={200}
                color="muted"
                block
                testId={`destination-credential-expiry-${section}`}
              >
                {expirySentence(expiry)}
              </SkyText>
            ) : (
              <CredentialExpiryEditor
                label={BINDING_EXPIRY_FIELD_LABELS[field]}
                expiry={expiry}
                canEdit={canEdit}
                pending={setExpiry.isPending}
                onSave={(expiresOn) => setExpiry.mutateAsync({ field, expiresOn })}
                testId={`destination-credential-expiry-${section}`}
              />
            )}
          </div>
        );
      })}
    </SkyCard>
  );
}
